import { planErrorMessage } from '@/lib/plan-errors';

const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: { field: string; message: string }[],
    /** Raw error envelope, for structured refusals. */
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The envelope's string `code` (e.g. `PLAN_LIMIT_REACHED`), when the API sent one. */
  get code(): string | undefined {
    return typeof this.body?.code === 'string' ? this.body.code : undefined;
  }
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  /**
   * When true, `body` is sent as-is (e.g. FormData) without JSON serialization,
   * and the Content-Type header is left to the browser so it can set the
   * correct multipart boundary.
   */
  formData?: boolean;
}

export async function apiClient<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuth = false, formData = false } = options;

  const requestHeaders: Record<string, string> = { ...headers };
  if (!formData) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const requestBody = body !== undefined && body !== null
    ? (formData ? (body as BodyInit) : JSON.stringify(body))
    : undefined;

  let res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: requestHeaders,
    credentials: 'include',
    body: requestBody,
  });

  // On 401, try refreshing the token once
  if (res.status === 401 && !skipAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: requestHeaders,
        credentials: 'include',
        body: requestBody,
      });
    } else {
      localStorage.removeItem('bbpos_user');
      window.location.assign('/login?reason=session-expired');
      throw new ApiError(401, 'Session expired. Please log in again.');
    }
  }

  const json = await res.json().catch(() => null);

  if (!res.ok) {
    const message =
      json?.message ??
      json?.error ??
      `Request failed with status ${res.status}`;
    const errorMessage =
      planErrorMessage(json) ?? (Array.isArray(message) ? message.join(', ') : message);
    throw new ApiError(res.status, errorMessage, json?.details, json ?? undefined);
  }

  if (json.meta !== undefined) {
    return { data: json.data, meta: json.meta } as T;
  }
  return json.data as T;
}

/**
 * Fetch a binary blob (PDF, image, etc.) with auth handling.
 * Reuses the same cookie-based auth as apiClient.
 */
export async function apiClientBlob(
  path: string,
): Promise<Blob> {
  let res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });

  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
    } else {
      localStorage.removeItem('bbpos_user');
      window.location.assign('/login?reason=session-expired');
      throw new ApiError(401, 'Session expired. Please log in again.');
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, `Download failed with status ${res.status}`);
  }

  return res.blob();
}
